import {
    SxProps,
    Theme,
    Typography,
    Stack,
    TableHead,
    styled,
    TableCell,
    Pagination,
    TableRow,
    Table
} from '@mui/material';
import Card from '../Utils/Card';
import CompletedReviewListing from './CompletedReviewListing';
import { CompletedReviewModel } from '../../lib/models/CompletedReviewModel';
import useGetPaginatedData from '../../lib/hooks/use-pagination-data';
import theme from '../../theme';
import { useScreenLargerThan } from '../../lib/hooks/breakpoint-hooks';
import { useEffect } from 'react';
import useTrackEvent from '../../lib/hooks/use-track-event';

export type CompletedReviewProps = {
    totalCompletedReviewHours: string;
    currentCycleId: string;
};

const CompletedReviews = ({ currentCycleId, totalCompletedReviewHours }: CompletedReviewProps) => {
    const trackEvent = useTrackEvent();
    const { data, paginationHandler, status, loading } = useGetPaginatedData<CompletedReviewModel>(
        `completed-review/invoice-cycle/${currentCycleId}`
    );

    const isLargerThanMedium = useScreenLargerThan('md');
    const isLargerThanLarge = useScreenLargerThan('lg');

    useEffect(() => {
        if (!loading) {
            if (status >= 200 && status < 400 && data) {
                trackEvent('Completed Reviews Data', {
                    status: 'success',
                    ancillaryHours: data.data.map(
                        ({ caseNumber, reviewedMinutes, caseStage, date }) => ({
                            caseNumber,
                            reviewedMinutes,
                            caseStage,
                            date
                        })
                    ),
                    page: data.page,
                    currentCycleId
                });
            } else {
                trackEvent('Completed Reviews Data', {
                    status: 'failure',
                    message: 'Completed Reviews API call response is neither 200 nor data exist'
                });
            }
        }
    }, [loading, status, data, trackEvent, currentCycleId]);

    const TableHeaderCell = styled(TableCell)({
        color: theme.palette.cardSubtitle.main,
        fontSize: 13,
        borderBottom: 'none'
    });

    return (
        <Stack>
            <Typography
                sx={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: '#232A3F',
                    paddingLeft: '0.938rem'
                }}>
                Completed Reviews
            </Typography>
            <Typography
                sx={{
                    fontSize: 12,
                    fontWeight: 400,
                    color: '#979797',
                    paddingLeft: '0.938rem',
                    paddingBottom: '0.938rem'
                }}>
                {totalCompletedReviewHours} Total Hours
            </Typography>

             <Card sx={cardStyle(isLargerThanLarge)}>
                <Card.Body>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {isLargerThanMedium && <TableHeaderCell>Date</TableHeaderCell>}
                                {isLargerThanMedium && (
                                    <TableHeaderCell>Case Number</TableHeaderCell>
                                )}
                                {isLargerThanMedium && (
                                    <TableHeaderCell>Case Stage</TableHeaderCell>
                                )}
                                {isLargerThanMedium && (
                                    <TableHeaderCell>UM Review Minutes</TableHeaderCell>
                                )}
                                {isLargerThanMedium && (
                                    <TableHeaderCell>Case Category</TableHeaderCell>
                                )}
                            </TableRow>
                        </TableHead>
                        {data && <CompletedReviewListing data={data.data} />}
                    </Table>
                </Card.Body>
                {data && (
                    <Pagination
                        shape="rounded"
                        page={data.page}
                        count={data.totalPages}
                        onChange={paginationHandler}
                        sx={{
                            paddingTop: '1.25rem',
                            paddingLeft: isLargerThanMedium ? '1rem' : '0'
                        }}
                    />
                )}
            </Card>
        </Stack>
    );
};
export default CompletedReviews;

const cardStyle: (media: boolean) => SxProps<Theme> = (media: boolean) => {
    const biggerThanSm: SxProps<Theme> = { width: '100%' };
    const smallerThanSm: SxProps<Theme> = {
        borderRadius: 0
    };

    return media ? biggerThanSm : { ...biggerThanSm, ...smallerThanSm };
};
